<template>
  <content-dialog
    v-if="serviceTask"
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :small-title="$vuetify.breakpoint.xsOnly"
    :persistent="$store.state.ui.alert.active"
    max-width="900px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <div class="mb-1">
        {{ serviceTask.title }}
        <span class="font-weight-regular grey--text">#{{ serviceTask.id }}</span>
      </div>

      <div class="body-1 grey--text">
        <v-chip
          small
          outlined
          :color="serviceTask.finished ? 'success' : 'grey'"
        >
          <v-icon
            style="font-size: 12px"
            :color="serviceTask.finished ? 'success' : 'grey'"
            class="mr-1"
          >
            fa fa-circle
          </v-icon>
          {{ serviceTask.finished ? 'Finished' : 'Pending' }}
        </v-chip>
        <yacht-chip
          class="ml-1"
          :name="serviceTask.yacht.name"
          :image-path="serviceTask.yacht.model.main_image.path"
          small
        />
        &middot;
        created
        <span v-if="serviceTask.created_by">
          by
          <v-chip
            small
          >
            <v-icon
              left
              x-small
            >
              fa fa-user
            </v-icon>
            {{ serviceTask.created_by.name }}
          </v-chip>
        </span>
        on
        {{ serviceTask.created_at | moment('D MMM YYYY') }}
      </div>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text class="pa-0">
      <v-row
        class="fill-height"
        no-gutters
      >
        <v-col
          lg="12"
          cols="16"
          class="pa-6 pb-10"
          style="overflow-y: auto"
        >
          <div class="mb-2">
            <v-chip
              v-if="serviceTask.priority"
              small
              dark
              outlined
              class="ma-0 mr-1"
              :color="serviceTaskClass.priorityData[serviceTask.priority].color"
            >
              {{ serviceTaskClass.priorityData[serviceTask.priority].label }}
            </v-chip>
            <v-chip
              outlined
              small
              class="ma-0 mr-1"
            >
              <v-icon
                small
                class="mr-1"
              >
                {{ serviceTask.owner_visible ? 'visibility' : 'visibility_off' }}
              </v-icon>
              {{ serviceTask.owner_visible ? 'External Use' : 'Internal Use' }}
            </v-chip>
            <v-chip
              outlined
              small
              class="ma-0 mr-1 text-capitalize"
            >
              {{ serviceTask.category }}
            </v-chip>
          </div>

          <div class="mb-4 grey--text text--darken-1">
            {{ serviceTask.description }}
          </div>

          <v-alert
            v-if="serviceTask.request_status === serviceTaskClass.REQUEST_STATUS_DENIED"
            outlined
            color="error"
            dense
            class="caption mb-4"
          >
            <strong>DECLINED &middot; REASON:</strong> {{ serviceTask.request_declined_reason }}
          </v-alert>

          <div v-if="serviceTask.finished">
            <div class="subtitle-1 font-weight-bold grey--text text--darken-2 mb-2">
              <v-avatar
                color="success"
                size="18px"
                class="mr-1"
              >
                <v-icon
                  color="white"
                  style="font-size: 12px"
                >
                  check
                </v-icon>
              </v-avatar>
              Service has been carried out
            </div>

            <div>
              <v-row
                no-gutters
                align="center"
                class="list--bordered py-2"
              >
                <v-col
                  cols="4"
                  class="grey--text caption"
                >
                  Date
                </v-col>
                <v-col cols="12">
                  {{ serviceTask.finished_at | moment('D MMM YYYY') }}
                </v-col>
              </v-row>
              <v-row
                no-gutters
                align="center"
                class="list--bordered py-2"
              >
                <v-col
                  cols="4"
                  class="grey--text caption"
                >
                  Hours spent
                </v-col>
                <v-col cols="12">
                  {{ serviceTask.total_hours_spent }} h
                </v-col>
              </v-row>
              <v-row
                no-gutters
                align="center"
                class="list--bordered py-2"
              >
                <v-col
                  cols="4"
                  class="grey--text caption"
                >
                  Serviced by
                </v-col>
                <v-col cols="12">
                  <v-chip
                    v-for="(assignee, i) in serviceTask.assigned_to"
                    :key="i"
                    small
                    outlined
                    color="grey darken-2"
                    class="ma-0 mr-2"
                  >
                    <v-icon
                      class="mr-1"
                      style="font-size: 14px"
                    >
                      {{ serviceTaskClass.assigneeTypeData[assignee.type].icon }}
                    </v-icon>
                    {{ assignee.name }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                align="center"
                class="list--bordered py-2"
              >
                <v-col
                  cols="4"
                  class="grey--text caption"
                >
                  Marked as finished by
                </v-col>
                <v-col cols="12">
                  <v-chip
                    v-if="serviceTask.finished_by_staff"
                    small
                    outlined
                    color="grey darken-2"
                    class="ma-0 mr-2"
                  >
                    <v-icon
                      class="mr-1"
                      style="font-size: 14px"
                    >
                      {{ serviceTaskClass.assigneeTypeData['staff'].icon }}
                    </v-icon>
                    {{ serviceTask.finished_by_staff.name }}
                  </v-chip>
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="subtitle-1 font-weight-bold grey--text text--darken-2 mb-2 mt-6">
            Work Logs
          </div>
          <div
            v-if="workLogs.length === 0"
            class="grey lighten-3 text-center caption grey--text pa-4 radius-10"
          >
            No Work Logs
          </div>
          <v-card
            v-else
            class="v-card--bordered radius-5 pa-2"
          >
            <v-row
              v-for="workLog in workLogs"
              :key="'workLog-'+workLog.id"
              class="list--bordered pa-1 caption"
              no-gutters
            >
              <v-col
                cols="auto"
                class="text-no-wrap"
              >
                <strong>{{ workLog.done_at | moment('D MMM YYYY') }}</strong>
              </v-col>
              <v-col class="px-3">
                {{ workLog.done_by.name }}
              </v-col>
              <v-col cols="auto">
                {{ workLog.hours_spent }}h
              </v-col>
            </v-row>
          </v-card>

          <div class="subtitle-1 font-weight-bold grey--text text--darken-2 mb-2 mt-6">
            Parts used
          </div>
          <div
            v-if="serviceTask.inventory_items.length === 0 && serviceTask.custom_parts.length === 0"
            class="grey lighten-3 text-center caption grey--text pa-4 radius-10"
          >
            No parts used
          </div>
          <v-card
            v-else
            class="v-card--bordered radius-5 pa-4"
          >
            <v-row
              v-for="(item, i) in serviceTask.inventory_items"
              :key="i"
              no-gutters
              class="list--bordered py-1"
            >
              <v-col>
                <div>
                  {{ item.label }}
                </div>
                <div class="caption grey--text">
                  {{ item.code }}
                </div>
              </v-col>
              <v-col
                class="text-no-wrap"
                cols="auto"
              >
                {{ item.quantity }} {{ item.unit }}
              </v-col>
            </v-row>
            <v-row
              v-for="(item, i) in serviceTask.custom_parts"
              :key="'custom_part_+'+i"
              no-gutters
              class="list--bordered py-1"
            >
              <v-col>
                <div>
                  {{ item.label }}
                </div>
                <div class="caption grey--text">
                  {{ item.code }}
                </div>
              </v-col>
              <v-col
                class="text-no-wrap"
                cols="auto"
              >
                {{ item.quantity }} {{ item.unit }}
              </v-col>
            </v-row>
          </v-card>

          <div class="subtitle-1 font-weight-bold grey--text text--darken-2 mb-2 mt-6">
            Files
          </div>
          <div
            v-if="serviceTask.files.length === 0"
            class="grey lighten-3 text-center caption grey--text pa-4 radius-10"
          >
            No files attached
          </div>
          <v-row v-else>
            <v-col
              v-for="file in serviceTask.files"
              :key="file.id"
              class="d-flex clickable"
              cols="4"
              @click="$store.dispatch('previewImage', file.path)"
            >
              <v-img
                :src="file.path"
                class="grey lighten-2"
                height="150px"
              />
            </v-col>
          </v-row>

          <div class="subtitle-1 font-weight-bold grey--text text--darken-2 mb-2 mt-6">
            Other
          </div>

          <v-chip
            class="mr-1"
            outlined
            :color="serviceTask.deposit_insurance ? 'grey darken-3' : 'grey lighten-1'"
          >
            <v-icon
              x-small
              class="mr-1"
            >
              fa {{ serviceTask.deposit_insurance ? 'fa-check' : 'fa-times' }}
            </v-icon>
            Damage by charter client
          </v-chip>
          <v-chip
            class="mr-1"
            outlined
            :color="serviceTask.hull_insurance ? 'grey darken-3' : 'grey lighten-1'"
          >
            <v-icon
              x-small
              class="mr-1"
            >
              fa {{ serviceTask.hull_insurance ? 'fa-check' : 'fa-times' }}
            </v-icon>
            Hull Insurance
          </v-chip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-chip
                class="mr-1"
                outlined
                :color="serviceTask.is_warranty_claim ? 'grey darken-3' : 'grey lighten-1'"
                v-on="serviceTask.warranty_claim_resolution_at ? on : undefined"
              >
                <v-icon
                  x-small
                  class="mr-1"
                >
                  fa {{ serviceTask.is_warranty_claim ? 'fa-check' : 'fa-times' }}
                </v-icon>
                Warranty Claim
                <span
                  v-if="serviceTask.warranty_claim_resolution === true"
                  class="success--text font-weight-bold ml-2"
                >
                  Accepted
                </span>
                <span
                  v-else-if="serviceTask.warranty_claim_resolution === false"
                  class="error--text font-weight-bold ml-2"
                >
                  Declined
                </span>
              </v-chip>
            </template>
            <span v-if="serviceTask.warranty_claim_resolution_at">
              Marked by <strong>{{ serviceTask.warranty_claim_resolution_by ? serviceTask.warranty_claim_resolution_by.name : 'n/a' }}</strong> on
              <strong>
                {{ serviceTask.warranty_claim_resolution_at | moment('D MMM YYYY HH:mm:ss') }}
              </strong>
            </span>
          </v-tooltip>
          <v-chip
            class="mr-1"
            outlined
            :color="serviceTask.is_intervention ? 'grey darken-3' : 'grey lighten-1'"
          >
            <v-icon
              x-small
              class="mr-1"
            >
              fa {{ serviceTask.is_intervention ? 'fa-check' : 'fa-times' }}
            </v-icon>
            Service Call
          </v-chip>

          <div v-if="serviceTask.warranty_insurance_note">
            <div class="subtitle-1 font-weight-bold grey--text text--darken-2 mb-2 mt-6">
              Warranty / Insurance Note
            </div>

            <v-card
              class="v-card--bordered radius-5 py-2 px-4 body-1 grey--text text--darken-2"
            >
              {{ serviceTask.warranty_insurance_note }}
            </v-card>
          </div>
        </v-col>
        <v-col
          lg="4"
          cols="16"
          class="grey lighten-4"
        >
          <v-list
            dense
            class="transparent"
          >
            <v-list-item
              v-if="serviceTask.request_status && $can('decide-service-requests', 'ServiceTask') && !serviceTask.finished && serviceTask.request_status !== serviceTaskClass.REQUEST_STATUS_ACCEPTED"
              @click="updateRequestStatus(serviceTaskClass.REQUEST_STATUS_ACCEPTED)"
            >
              <v-list-item-action>
                <v-icon
                  color="success"
                  small
                >
                  check
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title class="success--text">
                  Accept Request
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="serviceTask.request_status && $can('decide-service-requests', 'ServiceTask') && !serviceTask.finished && serviceTask.request_status !== serviceTaskClass.REQUEST_STATUS_DENIED"
              @click="updateRequestStatus(serviceTaskClass.REQUEST_STATUS_DENIED)"
            >
              <v-list-item-action>
                <v-icon
                  color="error"
                  small
                >
                  close
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title class="error--text">
                  Deny Request
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="$can('change-visibility-settings', 'ServiceTask') && !serviceTask.owner_visible"
              @click="updateOwnerVisible(true)"
            >
              <v-list-item-action>
                <v-icon small>
                  visibility
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>External Use</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="$can('change-visibility-settings', 'ServiceTask') && serviceTask.owner_visible"
              @click="updateOwnerVisible(false)"
            >
              <v-list-item-action>
                <v-icon small>
                  visibility_off
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Internal Use</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="$can('edit', 'ServiceTask') && !serviceTask.finished && serviceTask.request_status !== serviceTaskClass.REQUEST_STATUS_PENDING"
              @click="finishTask(true)"
            >
              <v-list-item-action>
                <v-icon small>
                  check
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Mark as Finished</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="$can('edit', 'ServiceTask') && serviceTask.finished"
              @click="unfinishTask"
            >
              <v-list-item-action>
                <v-icon small>
                  close
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Unmark as Finished</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="$can('edit', 'ServiceTask') && !serviceTask.finished && serviceTask.request_status !== serviceTaskClass.REQUEST_STATUS_PENDING"
              @click="addTimeLog"
            >
              <v-list-item-action>
                <v-icon small>
                  query_builder
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Add Work Log</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <!--            <v-list-item-->
            <!--              v-if="$can('edit', 'ServiceTask') && serviceTask.category !== serviceTaskClass.CATEGORY_SPECIFIC"-->
            <!--              @click="updateCategory(serviceTaskClass.CATEGORY_SPECIFIC)"-->
            <!--            >-->
            <!--              <v-list-item-action>-->
            <!--                <v-icon small>-->
            <!--                  share-->
            <!--                </v-icon>-->
            <!--              </v-list-item-action>-->
            <!--              <v-list-item-content>-->
            <!--                <v-list-item-title>Move to specific</v-list-item-title>-->
            <!--              </v-list-item-content>-->
            <!--            </v-list-item>-->
            <v-divider v-if="$can('edit', 'ServiceTask')" />
            <v-list-item
              v-if="$can('edit', 'ServiceTask')"
              @click="openEditDialog"
            >
              <v-list-item-action>
                <v-icon small>
                  edit
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Edit task</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="$can('edit', 'ServiceTask')" />
            <v-menu>
              <template v-slot:activator="{ on }">
                <v-list-item
                  v-if="$can('edit', 'ServiceTask')"
                  v-on="on"
                >
                  <v-list-item-action>
                    <v-icon small>
                      list
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Set Priority</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="priority in serviceTaskClass.priorityData"
                  :key="'priority_'+priority.key"
                  @click="setPriority(priority.key)"
                >
                  <v-list-item-action>
                    <v-avatar
                      size="10px"
                      :color="priority.color"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ priority.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-divider v-if="$can('delete', 'ServiceTask') && !serviceTask.finished" />
            <v-list-item
              v-if="$can('delete', 'ServiceTask') && !serviceTask.finished"
              :disabled="!!serviceTask.request_status"
              @click="deleteTask"
            >
              <v-list-item-action>
                <v-icon small>
                  delete
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ !!serviceTask.request_status ? 'N/A (Owner Request)' : 'Remove Task' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-card-text>
    <!--</editor-fold>-->

    <template v-slot:dialogActions="{}">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Close
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ServiceTask from '@/models/ServiceTask'
import ContentDialog from '@/components/helpers/Dialog'
import YachtChip from '@/components/service/helpers/YachtChip'

export default {
    name: 'ServiceTaskViewDialog',
    components: {
      YachtChip,
      ContentDialog
    },
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      serviceTask: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        serviceTaskClass: null,
        workLogs: []
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      }
    },
    watch: {
      modalVisible(value) {
        if (!value) return

        this.fetchWorkLogs()
      },
      serviceTask(value) {
        if (!value) return

        this.fetchWorkLogs()
      }
    },
    created() {
      this.serviceTaskClass = ServiceTask

      this.fetchWorkLogs()
    },
    methods: {
      updateRequestStatus(val) {
        this.$emit('update:requestStatus', val)
      },
      updateOwnerVisible(val) {
        this.$emit('update:ownerVisible', val, this.serviceTask.id)
      },
      finishTask() {
        this.$emit('serviceTask:finish', this.serviceTask)
      },
      unfinishTask() {
        this.$emit('serviceTask:unfinish', this.serviceTask.id)
      },
      updateCategory(val) {
        this.$emit('update:category', val, this.serviceTask.id)
      },
      openEditDialog() {
        this.$emit('serviceTask:edit', this.serviceTask)
      },
      deleteTask() {
        this.$emit('serviceTask:delete', this.serviceTask.id)
      },
      addTimeLog() {
        this.$emit('serviceTask:logTime', this.serviceTask)
      },
      async fetchWorkLogs() {
        if (!this.serviceTask) return

        try {
          this.workLogs = await ServiceTask.api.fetchWorkLogs(this.serviceTask.id)
        } catch (e) {
          throw e
        }
      },
      setPriority(priority) {
        this.$emit('serviceTask:setPriority', priority, this.serviceTask.id)
      }
    }
  }
</script>
